import { useEffect, useState } from "react";
import {
  Alert,
  AlertColor,
  Box,
  Button,
  Checkbox,
  Snackbar,
  Tooltip,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import SDCRegistrationModal from "./SDCRegistrationModal";
import { getJwtTokenFromIdToken } from "../auth/AuthFanc";
import UpdateButton from "../../atoms/Button/UpdateButton";
import {
  CustomContainer,
  StyledDataGrid,
  customDataGridLocaleTextAll,
  customDataGridLocaleTextLoading,
  customDataGridLocaleTextNoData,
} from "../../../styles/styles";
import IconButtonWithTooltip from "../../atoms/Button/IconButtonWithTooltip";
import EditIcon from "@mui/icons-material/Edit";
import LeaderboardRoundedIcon from "@mui/icons-material/LeaderboardRounded";
import { CircleOutlined, Close, CloudUpload } from "@mui/icons-material";
import ftsmartkeyIcon from "../../../assets/images/ftsmartkey_icon_small.png";
import mirairodoorIcon from "../../../assets/images/mirairodoor_icon_small.png";

const enum EnabledService {
  None = 0,
  AalOnly,
  SciOnly,
  Both,
}

interface SDC {
  autoSdcId: number;
  sdcUuid: string;
  sdcSerialNumber: string;
  simId: string;
  shippingDate: string;
  firmwareVersion: string;
  isEnabled: boolean;
  memo: string;
  createdAt: string;
  isOnline: boolean;
  enabledService: EnabledService;
  lastDownloadedAt: Date;
  building:
    | {
        buildingName: string;
        buildingAddress: string;
        owner: {
          ownerName: string;
        };
      }
    | undefined;
}

const SDCListPage = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<SDC[]>([]);
  const [checkedSdcUuids, setCheckedSdcUuids] = useState<Set<string>>(
    new Set<string>()
  );
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      // JWTトークンを取得します
      const jwtToken = await getJwtTokenFromIdToken();

      try {
        // call indexSdcs API
        const response = await fetch(
          `${process.env.REACT_APP_API_ENDPOINT}/sdcs`,
          {
            headers: {
              Authorization: jwtToken, // Authorizationヘッダーを追加
            },
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        setData(
          data.sdcs.map((sdc: any) => {
            let enabledService = EnabledService.None;
            if (sdc.isAalEnabled && sdc.isSciEnabled) {
              enabledService = EnabledService.Both;
            } else if (sdc.isAalEnabled) {
              enabledService = EnabledService.AalOnly;
            } else if (sdc.isSciEnabled) {
              enabledService = EnabledService.SciOnly;
            }
            const datum: SDC = {
              ...sdc,
              enabledService: enabledService,
            };
            return datum;
          })
        );
        setIsLoading(false);
      } catch (error) {
        setIsLoading(false);
        console.error(error);
      }
    };

    fetchData();
  }, []);

  const handleDetailClick = (autoSdcId: string) => {
    navigate(`/sdcs/${autoSdcId}/edit`);
    console.log(`SDC詳細 button clicked for SDC: ${autoSdcId}`);
  };

  const handleLogClick = (autoSdcId: string) => {
    navigate(`/logs/${autoSdcId}`);
    console.log(`Log button clicked for SDC: ${autoSdcId}`);
  };

  const [openModal, setOpenModal] = useState(false);

  // アラート
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState<AlertColor>("success");

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
    // window.location.reload();//下部アラートが消えた後に画面リロード
  };

  const columns: GridColDef[] = [
    {
      field: "check",
      headerName: "選択",
      flex: 0.5,
      renderCell: (params: GridRenderCellParams<SDC>) => (
        <Checkbox
          checked={checkedSdcUuids.has(params.row.sdcUuid)}
          onChange={(e) => {
            setCheckedSdcUuids((oldSet) => {
              const newSet = new Set(oldSet);
              if (e.target.checked) {
                newSet.add(params.row.sdcUuid);
              } else {
                newSet.delete(params.row.sdcUuid);
              }
              return newSet;
            });
          }}
        />
      ),
    },
    {
      field: "sdcSerialNumber",
      headerName: "SDCSN",
      flex: 1,
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: "sdcUuid",
      headerName: "SDCUUID",
      flex: 2,
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: "simId",
      headerName: "SIMID",
      flex: 1.5,
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: "shippingDate",
      headerName: "出荷日",
      flex: 0.7,
      renderCell: (params) => {
        const dateStr = new Date(params.value).toLocaleDateString("ja-JP", {
          timeZone: "Asia/Tokyo",
        });
        return (
          <Tooltip title={dateStr}>
            <span>{dateStr}</span>
          </Tooltip>
        );
      },
    },
    {
      field: "isOnline",
      headerName: "接続状態",
      flex: 0.7,
      renderCell: (params) => (
        <Tooltip title={params.value ? "オンライン" : "オフライン"}>
          {params.value ? (
            <CircleOutlined sx={{ color: "green" }} />
          ) : (
            <Close sx={{ color: "red" }} />
          )}
        </Tooltip>
      ),
    },
    {
      field: "firmwareVersion",
      headerName: "FirmVer",
      flex: 1,
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: "buildingName",
      headerName: "取付先名（物件名称）",
      flex: 1,
      valueGetter: (params: any) => params.row.building?.buildingName || "",
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: "buildingAddress",
      headerName: "設置物件住所",
      flex: 1,
      valueGetter: (params: any) => params.row.building?.buildingAddress || "",
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: "ownerName",
      headerName: "LAD契約者（オーナー）名称",
      flex: 1,
      valueGetter: (params: any) => params.row.building?.owner?.ownerName || "",
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: "enabledService",
      headerName: "有効サービス",
      flex: 1,
      renderCell: (params) => {
        const enabledService: EnabledService = params.value;
        return (
          <div>
            {(enabledService === EnabledService.AalOnly ||
              enabledService === EnabledService.Both) && (
              <Tooltip title="FTスマートキー">
                <img
                  src={ftsmartkeyIcon}
                  alt="FTスマートキー icon"
                  style={{
                    height: "30px",
                    marginRight: "3px",
                  }}
                />
              </Tooltip>
            )}
            {(enabledService === EnabledService.SciOnly ||
              enabledService === EnabledService.Both) && (
              <Tooltip title="ミライロドア">
                <img
                  src={mirairodoorIcon}
                  alt="ミライロドア icon"
                  style={{
                    height: "30px",
                  }}
                />
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      field: "lastDownloadedAt",
      headerName: "SDCが最後に鍵をDLした日時",
      flex: 1.5,
      renderCell: (params) => {
        if (!params.value) {
          return (
            <Tooltip title="記録なし">
              <span>記録なし</span>
            </Tooltip>
          );
        }

        const dateStr = new Date(params.value).toLocaleString("ja-JP", {
          timeZone: "Asia/Tokyo",
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        });
        return (
          <Tooltip title={dateStr}>
            <span>{dateStr}</span>
          </Tooltip>
        );
      },
    },
    {
      field: "memo",
      headerName: "メモ",
      flex: 1,
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: "autoSdcId",
      headerName: "操作",
      flex: 2,
      renderCell: (params: any) => (
        <>
          <IconButtonWithTooltip
            title="詳細"
            onClick={() => handleDetailClick(params.row.autoSdcId)}
            icon={<EditIcon />}
          />
          <IconButtonWithTooltip
            title="ログ"
            onClick={() => handleLogClick(params.row.autoSdcId)}
            icon={<LeaderboardRoundedIcon />}
          />
        </>
      ),
    },
  ];

  return (
    <CustomContainer maxWidth={false}>
      <Box display="flex" flexDirection="column" marginBottom={2}>
        <Typography variant="h5" component="h2" gutterBottom>
          出荷済みSDC一覧
        </Typography>
        <Box display="flex" justifyContent="flex-end">
          <Button
            variant="outlined"
            color="primary"
            onClick={() => setOpenModal(true)}
            sx={{ mr: 1 }}
          >
            新規登録
          </Button>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => {
              navigate("/ota-schedules/new", {
                state: {
                  targetSdcUuids: Array.from(checkedSdcUuids),
                },
              });
            }}
            endIcon={<CloudUpload />}
            sx={{ mr: 1 }}
          >
            新規OTA実行予約
          </Button>
          <Box>
            <UpdateButton />
          </Box>
        </Box>
      </Box>
      <div style={{ height: 700, width: "100%" }}>
        <StyledDataGrid
          rows={data}
          columns={columns}
          getRowId={(row) => row.autoSdcId}
          disableRowSelectionOnClick
          localeText={
            isLoading
              ? customDataGridLocaleTextLoading
              : data.length === 0
              ? customDataGridLocaleTextNoData
              : customDataGridLocaleTextAll
          }
        />
      </div>

      <SDCRegistrationModal
        open={openModal}
        setOpen={setOpenModal}
        setSnackbarOpen={setSnackbarOpen}
        setAlertSeverity={setAlertSeverity}
        setSnackbarMessage={setSnackbarMessage}
      />

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={alertSeverity}
          variant="filled"
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </CustomContainer>
  );
};

export default SDCListPage;
